import { Camera } from '@capacitor/camera';

import { isNative } from '../native/platform';

import { CAMERA_CONSTRAINTS, type MediaDevicesLike } from './barcode';

/**
 * BP-11 R10: one camera permission path for the browser and for the app.
 *
 * R10: "On web → `navigator.mediaDevices`. On native → `@capacitor/camera`
 * `checkPermissions/requestPermissions({permissions:['camera']})` first (so
 * the OS prompt carries our copy), then `getUserMedia` inside the WebView."
 *
 * **Why the plugin first and `getUserMedia` second.** On Android the WebView
 * cannot grant itself the OS permission: `getUserMedia` inside it fails with
 * `NotAllowedError` until the *app* holds `CAMERA`, and the prompt the
 * WebView would raise carries Chrome's wording about a website. Asking
 * through the plugin puts our own `NSCameraUsageDescription` /
 * `AndroidManifest` copy on the dialog, which is also what a store reviewer
 * reads. Once it is granted, the stream comes from `getUserMedia` as it does
 * in a browser — so the preview element, the constraints and the teardown
 * are the same code on all three platforms.
 */

/** R10's three answers. `prompt` means "ask, and the ask has not happened". */
export type CameraPermission = 'granted' | 'denied' | 'prompt';

/** The slice of `@capacitor/camera` this module uses; injected in tests. */
export interface CameraPlugin {
  checkPermissions: typeof Camera.checkPermissions;
  requestPermissions: typeof Camera.requestPermissions;
}

/** What `openStream` gives back: a stream, or why there is none. */
export type StreamResult =
  | { stream: MediaStream; problem?: undefined }
  | { stream?: undefined; problem: CameraPermission | 'unsupported' | 'unavailable' };

/**
 * Narrow Capacitor's `CameraPermissionState` onto R10's three.
 *
 * iOS can answer `limited`, which is about the *photo library* and never
 * about the camera; it is read as `granted` because a limited grant still
 * opens the camera, and reading it as `denied` would send somebody to the
 * settings app to fix something that is not broken.
 */
export function narrowPermission(state: string): CameraPermission {
  if (state === 'granted' || state === 'limited') {
    return 'granted';
  }
  return state === 'denied' ? 'denied' : 'prompt';
}

/**
 * R10's `ensureCameraPermission`.
 *
 * In a browser there is nothing to ask: the permission prompt belongs to
 * `getUserMedia`, and answering `prompt` here is the honest answer — the
 * question has not been put yet.
 */
export async function ensureCameraPermission(
  plugin: CameraPlugin = Camera,
): Promise<CameraPermission> {
  if (!isNative()) {
    return 'prompt';
  }
  const current = narrowPermission((await plugin.checkPermissions()).camera);
  if (current !== 'prompt') {
    return current;
  }
  const asked = await plugin.requestPermissions({ permissions: ['camera'] });
  return narrowPermission(asked.camera);
}

/**
 * R10's `openStream`: the permission, then the stream.
 *
 * Returns a problem rather than throwing, because every caller has a screen
 * to draw for each case — §6's `/try-on` states are "prompt, granted,
 * denied, no-camera" — and an exception would make three of the four look
 * like a crash.
 */
export async function openStream(
  devices: MediaDevicesLike | undefined,
  constraints: MediaStreamConstraints = CAMERA_CONSTRAINTS,
  plugin: CameraPlugin = Camera,
): Promise<StreamResult> {
  const permission = await ensureCameraPermission(plugin);
  if (permission === 'denied') {
    return { problem: 'denied' };
  }
  if (devices === undefined) {
    return { problem: 'unsupported' };
  }
  try {
    return { stream: await devices.getUserMedia(constraints) };
  } catch (error) {
    const name = error instanceof Error ? error.name : '';
    return { problem: name === 'NotAllowedError' ? 'denied' : 'unavailable' };
  }
}
